/**
 * CE.SDK Placeholders Video Editor - Initialization Module
 *
 * This module demonstrates the CE.SDK placeholder feature for video editing:
 * - Enable placeholder creation for template designers (Creator role)
 * - Placeholder regions can be defined for video clips, images, and other elements
 * - Adopters can fill placeholders with their own content
 *
 * @see https://img.ly/docs/cesdk/js/create-templates/add-dynamic-content/placeholders-d9ba8a/
 */

import type CreativeEditorSDK from '@cesdk/cesdk-js';

import {
  BlurAssetSource,
  CaptionPresetsAssetSource,
  ImageColorsAssetSource,
  ColorPaletteAssetSource,
  CropPresetsAssetSource,
  DemoAssetSources,
  EffectsAssetSource,
  FiltersAssetSource,
  PagePresetsAssetSource,
  StickerAssetSource,
  TextComponentAssetSource,
  TypefaceAssetSource,
  TextAssetSource,
  VectorShapeAssetSource,
  UploadAssetSources
} from '@cesdk/cesdk-js/plugins';

import { AdvancedVideoEditorConfig } from './config/advanced-video-editor/plugin';
import { VideoEditorConfig } from './config/video-editor/plugin';
import TemplatesApi from '../templates/Templates.api';

// ============================================================================
// Creator Editor Initialization
// ============================================================================

/**
 * Initialize the CE.SDK Placeholders Video Editor for Creator role.
 *
 * Creator mode enables:
 * - Advanced video editor configuration with full timeline control
 * - Dark theme for professional editing environment
 * - Placeholder creation features for defining template regions
 * - Inspector panel for detailed block properties
 *
 * @param cesdk - The CreativeEditorSDK instance to configure
 */
export async function initVideoPlaceholdersCreatorEditor(
  cesdk: CreativeEditorSDK,
  options: { skipEmptyScene?: boolean } = {}
) {
  disposeVideoPlaceholdersCreatorEditor();

  // ============================================================================
  // Configuration Plugin (Advanced Video Editor)
  // ============================================================================

  await cesdk.addPlugin(new AdvancedVideoEditorConfig());

  // ============================================================================
  // Theme Configuration (Runtime API)
  // ============================================================================

  cesdk.ui.setTheme('dark');

  // ============================================================================
  // Asset Source Plugins
  // ============================================================================

  await cesdk.addPlugin(new BlurAssetSource());
  await cesdk.addPlugin(new CaptionPresetsAssetSource());
  await cesdk.addPlugin(new ImageColorsAssetSource());
  await cesdk.addPlugin(new ColorPaletteAssetSource());
  await cesdk.addPlugin(new CropPresetsAssetSource());

  await cesdk.addPlugin(
    new UploadAssetSources({
      include: [
        'ly.img.image.upload',
        'ly.img.video.upload',
        'ly.img.audio.upload'
      ]
    })
  );

  await cesdk.addPlugin(
    new DemoAssetSources({
      include: [
        'ly.img.templates.video.*',
        'ly.img.image.*',
        'ly.img.video.*'
      ]
    })
  );

  await cesdk.addPlugin(new EffectsAssetSource());
  await cesdk.addPlugin(new FiltersAssetSource());

  await cesdk.addPlugin(
    new PagePresetsAssetSource({
      include: [
        'ly.img.page.presets.instagram.*',
        'ly.img.page.presets.facebook.*',
        'ly.img.page.presets.x.*',
        'ly.img.page.presets.linkedin.*',
        'ly.img.page.presets.pinterest.*',
        'ly.img.page.presets.tiktok.*',
        'ly.img.page.presets.youtube.*',
        'ly.img.page.presets.video.*'
      ]
    })
  );

  await cesdk.addPlugin(new TextAssetSource());
  await cesdk.addPlugin(new TextComponentAssetSource());
  await cesdk.addPlugin(new TypefaceAssetSource());
  await cesdk.addPlugin(new VectorShapeAssetSource());

  cesdk.engine.editor.setRole('Creator');

  installMediaUriResolver(cesdk);
  try {
    applyAssetSources(cesdk, await TemplatesApi.getCesdkAssetSources());
  } catch (error) {
    console.warn('Failed to load custom CE.SDK asset sources:', error);
  }

  if (!options.skipEmptyScene) {
    // Mobile CE.SDK still requires legacy Video mode in saved scene strings.
    await cesdk.actions.run('scene.create', { mode: 'Video' });
  }
}

export function disposeVideoPlaceholdersCreatorEditor() {
  mediaObjectUrls.forEach((objectUrl) => URL.revokeObjectURL(objectUrl));
  mediaObjectUrls.length = 0;
  mediaCache.clear();
}

// ============================================================================
// Adopter Editor Initialization
// ============================================================================

/**
 * Initialize the CE.SDK Placeholders Video Editor for Adopter role.
 *
 * Adopter mode enables:
 * - Standard video editor configuration with simplified interface
 * - Light theme for content editing environment
 * - Placeholder content filling (placeholders created by Creator)
 *
 * @param cesdk - The CreativeEditorSDK instance to configure
 */
export async function initVideoPlaceholdersAdopterEditor(
  cesdk: CreativeEditorSDK
) {
  // ============================================================================
  // Configuration Plugin (Video Editor)
  // ============================================================================

  await cesdk.addPlugin(new VideoEditorConfig());

  // ============================================================================
  // Theme Configuration (Runtime API)
  // ============================================================================

  cesdk.ui.setTheme('light');

  // ============================================================================
  // Asset Source Plugins
  // ============================================================================

  await cesdk.addPlugin(new BlurAssetSource());
  await cesdk.addPlugin(new CaptionPresetsAssetSource());
  await cesdk.addPlugin(new ImageColorsAssetSource());
  await cesdk.addPlugin(new ColorPaletteAssetSource());
  await cesdk.addPlugin(new CropPresetsAssetSource());

  await cesdk.addPlugin(
    new UploadAssetSources({
      include: [
        'ly.img.image.upload',
        'ly.img.video.upload',
        'ly.img.audio.upload'
      ]
    })
  );

  await cesdk.addPlugin(
    new DemoAssetSources({
      include: [
        'ly.img.templates.video.*',
        'ly.img.image.*',
        'ly.img.audio.*',
        'ly.img.video.*'
      ]
    })
  );

  await cesdk.addPlugin(new EffectsAssetSource());
  await cesdk.addPlugin(new FiltersAssetSource());

  await cesdk.addPlugin(
    new PagePresetsAssetSource({
      include: [
        'ly.img.page.presets.instagram.*',
        'ly.img.page.presets.facebook.*',
        'ly.img.page.presets.x.*',
        'ly.img.page.presets.linkedin.*',
        'ly.img.page.presets.pinterest.*',
        'ly.img.page.presets.tiktok.*',
        'ly.img.page.presets.youtube.*',
        'ly.img.page.presets.video.*'
      ]
    })
  );

  await cesdk.addPlugin(new StickerAssetSource());
  await cesdk.addPlugin(new TextAssetSource());
  await cesdk.addPlugin(new TextComponentAssetSource());
  await cesdk.addPlugin(new TypefaceAssetSource());
  await cesdk.addPlugin(new VectorShapeAssetSource());

  cesdk.engine.editor.setRole('Adopter');
}

function buildStickerAssets(categories: any[] = []) {
  return categories.flatMap((category) => {
    const categoryId = category.identifier || category.name;
    return (category.items || [])
      .filter((item: any) => item.stickerURI)
      .map((item: any) => ({
        id: item.identifier || item.name,
        groups: [categoryId],
        label: { en: item.name || item.identifier },
        tags: { en: [category.name || categoryId, 'sticker'] },
        meta: {
          uri: item.stickerURI,
          thumbUri: item.thumbnailURI || item.stickerURI,
          fillType: '//ly.img.ubq/fill/image',
          mimeType: 'image/png',
          kind: 'sticker'
        }
      }));
  });
}

/**
 * Reads the pixel dimensions of a sticker image so CE.SDK can insert it at its
 * real aspect ratio instead of a square frame
 */
async function stickerDimensions(uri?: string) {
  if (!uri) return null;

  try {
    const { data, contentType } = await TemplatesApi.fetchCesdkMedia(uri);
    const objectUrl = URL.createObjectURL(
      new Blob([data], { type: contentType })
    );

    try {
      const image = new Image();
      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = reject;
        image.src = objectUrl;
      });

      if (!image.naturalWidth || !image.naturalHeight) return null;
      return { width: image.naturalWidth, height: image.naturalHeight };
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch (error) {
    console.warn('Failed to measure sticker dimensions:', uri, error);
    return null;
  }
}

function buildAudioAssets(categories: any[] = []) {
  return categories.flatMap((category) => {
    const categoryId = category.identifier || category.name;
    return (category.items || [])
      .filter((item: any) => item.audioURI)
      .map((item: any) => ({
        id: item.identifier || item.title,
        groups: [categoryId],
        label: { en: item.title || item.name || item.identifier },
        tags: { en: [category.name || categoryId, 'audio', item.artist || ''] },
        meta: {
          uri: item.audioURI,
          thumbUri: item.thumbnailURI || '',
          mimeType: 'audio/mpeg',
          title: item.title || item.name || item.identifier,
          artist: item.artist || '',
          duration: item.duration != null ? String(item.duration) : '',
          kind: 'audio'
        }
      }));
  });
}

function addLibrarySource(
  cesdk: CreativeEditorSDK,
  sourceId: string,
  mimeTypes: string[],
  assets: any[],
  categories: any[] = [],
  fallbackTitle: string,
  applyAsset?: (asset: any) => Promise<number | undefined>
) {
  cesdk.engine.asset.addLocalSource(sourceId, mimeTypes, applyAsset);
  assets.forEach((asset) => {
    cesdk.engine.asset.addAssetToSource(sourceId, asset);
  });
  cesdk.engine.asset.assetSourceContentsChanged(sourceId);

  const translations: Record<string, string> = {};
  categories.forEach((category) => {
    const group = category.identifier || category.name;
    const name = category.name || category.identifier;
    if (group && name) {
      translations[`libraries.${sourceId}.${group}.label`] = name;
    }
  });
  cesdk.i18n.setTranslations({ en: translations });
  cesdk.ui.updateAssetLibraryEntry(sourceId, {
    sourceIds: [sourceId],
    title: ({ group }: { group?: string }) =>
      (group && translations[`libraries.${sourceId}.${group}.label`]) ||
      fallbackTitle
  });
}

function applyAssetSources(
  cesdk: CreativeEditorSDK,
  assetSources: {
    stickerCategories?: any[];
    audioCategories?: any[];
  } = {}
) {
  addLibrarySource(
    cesdk,
    'ly.img.sticker',
    ['image/png', 'image/svg+xml', 'image/webp', 'image/jpeg'],
    buildStickerAssets(assetSources.stickerCategories),
    assetSources.stickerCategories,
    'Stickers',
    async (asset) => {
      const dimensions = await stickerDimensions(asset.meta?.uri);
      const block = await cesdk.engine.asset.defaultApplyAsset(
        dimensions ? { ...asset, meta: { ...asset.meta, ...dimensions } } : asset
      );
      if (block != null && cesdk.engine.block.supportsContentFillMode(block)) {
        cesdk.engine.block.setContentFillMode(block, 'Contain');
      }
      return block;
    }
  );
  addLibrarySource(
    cesdk,
    'ly.img.audio',
    ['audio/mpeg', 'audio/mp4', 'audio/wav', 'audio/ogg'],
    buildAudioAssets(assetSources.audioCategories),
    assetSources.audioCategories,
    'Audio'
  );
}

const mediaCache = new Map<string, Promise<string>>();
const mediaObjectUrls: string[] = [];

function installMediaUriResolver(cesdk: CreativeEditorSDK) {
  cesdk.engine.editor.setURIResolverAsync(async (uri, defaultURIResolver) => {
    if (
      !uri.includes('blob.core.windows.net') &&
      !uri.includes('azureedge.net')
    ) {
      return defaultURIResolver(uri);
    }

    if (!mediaCache.has(uri)) {
      mediaCache.set(
        uri,
        TemplatesApi.fetchCesdkMedia(uri)
          .then(({ data, contentType }) => {
            const objectUrl = URL.createObjectURL(
              new Blob([data], { type: contentType })
            );
            mediaObjectUrls.push(objectUrl);
            return objectUrl;
          })
          .catch((error) => {
            mediaCache.delete(uri);
            console.warn('Failed to proxy CE.SDK media URI:', uri, error);
            return defaultURIResolver(uri);
          })
      );
    }

    return mediaCache.get(uri) as Promise<string>;
  });
}
